import { inject, Injectable, NgZone } from '@angular/core';
import { Router }                     from '@angular/router';
import { MatDialog, MatDialogRef }    from '@angular/material/dialog';

import { InactivityDialogComponent }                   from '../../../shared/components/dialog/inactivity-dialog/inactivity-dialog.component';
import { environment }                                 from '../../../../environments/environment';
import { TimeUtils }                                   from '../../../shared/utils/time.utils';
import { interval, Observable, Subject, Subscription } from 'rxjs';
import { BroadCastService } from '../../broadcast.service';

const EventsToListenOn: string[] = ['wheel', 'keydown', 'mousedown'];


enum TimeoutEvents {
  USER_ACTION = 'user-action'
}

@Injectable({
  providedIn: 'root'
})
export class TimeoutService {
  private zone = inject(NgZone);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private broadcastService = inject(BroadCastService);

  private timerGranularity = TimeUtils.secondsToMillis(environment.idleTimerGranularity);
  private idleUserThresholdTime = TimeUtils.secondsToMillis(environment.idleUserThresholdSeconds);
  private inactivityLogoutTime = TimeUtils.minutesToMillis(environment.sessionExpirationTime);
  private inactivityWarning = TimeUtils.minutesToMillis(environment.inactivityWarningTime);

  private lastEventTimestamp: number;
  private dialogRef: MatDialogRef<InactivityDialogComponent>;
  private timerSubscription: Subscription;
  private onUserActionBound: EventListenerOrEventListenerObject;

  private sessionExpiredSubject = new Subject<void>();

  constructor() {
    this.broadcastService.messagesOfType(TimeoutEvents.USER_ACTION).subscribe(() => {
      this.updateLastAction(true);
      this.closeInactivityDialog();
    });
    this.sessionExpiredSubject.asObservable().subscribe(() => this.stopListening());
  }

  startListening(): void {
    this.zone.runOutsideAngular(() => {
      if (!this.timerSubscription) {
        this.updateLastAction();
        this.addEventsListeners();
        this.setupTimer();
      }
    });
  }

  stopListening(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;

      this.removeEventsListeners();
      this.closeInactivityDialog();
      }
  }

  public keepSessionActive() {
    this.updateLastAction();
  }

  public getLastUserEvent(): number {
    return this.lastEventTimestamp;
  }

  public observeSessionExpired(): Observable<void> {
    return this.sessionExpiredSubject.asObservable();
  }

  private addEventsListeners(): void {
    this.onUserActionBound = this.onUserAction.bind(this);

    for (const event of EventsToListenOn) {
      window.document.addEventListener(event, this.onUserActionBound);
    }
  }

  private removeEventsListeners(): void {
    for (const event of EventsToListenOn) {
      window.document.removeEventListener(event, this.onUserActionBound, false);
    }
  }

  private onUserAction(): void {
    if (!this.isInactivityDialogOpen()) {
      this.updateLastAction();
    }
  }

  private setupTimer(): void {
    if (!this.timerSubscription) {
      this.timerSubscription = interval(this.timerGranularity).subscribe(
        () => this.onTimerTick()
      );
    }
  }

  private onTimerTick(): void {
    if (!this.timerSubscription) {
      return;
    }

    const now = Date.now();

    const inactiveTime = now - this.lastEventTimestamp;
    const userIdle = inactiveTime >= this.inactivityWarning;
    const userIdleOverLimit = inactiveTime >= this.inactivityLogoutTime - this.idleUserThresholdTime;

    if (userIdleOverLimit) {
      this.redirectSessionExpired();
    } else if (userIdle && !environment['embedded']) {
      this.openInactivityDialog();
    }
  }

  private updateLastAction(skipPublishBroadcast = false): void {
    this.lastEventTimestamp = Date.now();

    if (!skipPublishBroadcast) {
      this.broadcastService.publish(TimeoutEvents.USER_ACTION);
    }
  }

  private openInactivityDialog(): void {
    if (this.isInactivityDialogOpen()) {
      return;
    }

    this.zone.run(() => {
      this.dialogRef = this.dialog.open(InactivityDialogComponent, {
        width: '40vw',
        disableClose: true,
        panelClass: 'pin-inactivity-dialog'
      });
    });

    this.dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.closeInactivityDialog();
        this.updateLastAction();
      }
    });
  }

  private closeInactivityDialog(): void {
    if (this.dialogRef) {
      this.zone.run(() => {
        this.dialogRef.close();
      });

      this.dialogRef = undefined;
    }
  }

  private isInactivityDialogOpen(): boolean {
    return !!this.dialogRef;
  }

  private redirectSessionExpired(): void {
    this.sessionExpiredSubject.next();

    this.zone.run(() => {
      this.router.navigate(['/logout'], {queryParams: {sessionExpired: true}}).then();
    });
  }
}
